import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { ChevronDownIcon } from "../components/icons";
import { useProperty } from "./property-context";

// Dev-time view-as override. When the real user is a landlord, the
// role pill in the header lets them flip the UI to render the tenant
// surfaces (Request maintenance button, AvailabilitySection, etc.)
// without having to sign in as a different account.
//
// The impersonation is anchored to a specific unit so a landlord can
// preview what a tenant of Upstairs sees vs. a tenant of Downstairs —
// state shape is `viewAsUnitId: string | null` where null = real
// landlord and a string = "impersonate a tenant of this unit".
//
// Data scope on the server isn't affected; the landlord still
// receives every row. The filter happens client-side in the lists
// that care about unit visibility (FAQ, Q&A, maintenance schedule).
//
// Persisted in localStorage so the choice survives page reloads.

type RealRole = "landlord" | "tenant" | null | undefined;
type EffectiveRole = "landlord" | "tenant" | null | undefined;

const ViewAsCtx = createContext<{
  viewAsUnitId: string | null;
  setViewAsUnitId: (v: string | null) => void;
} | null>(null);

const STORAGE_KEY = "maisons:viewAsUnitId";
const LEGACY_BOOL_KEY = "maisons:viewAsTenant";

export function ViewAsProvider({ children }: { children: ReactNode }) {
  const [viewAsUnitId, setViewAsUnitId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && stored.length > 0) return stored;
    // Legacy migration: the old key was a boolean. We don't know
    // which unit they were impersonating, so reset to "no override"
    // and let them re-pick from the dropdown.
    window.localStorage.removeItem(LEGACY_BOOL_KEY);
    return null;
  });

  useEffect(() => {
    if (viewAsUnitId === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, viewAsUnitId);
    }
  }, [viewAsUnitId]);

  return (
    <ViewAsCtx.Provider value={{ viewAsUnitId, setViewAsUnitId }}>
      {children}
    </ViewAsCtx.Provider>
  );
}

function useViewAs() {
  const ctx = useContext(ViewAsCtx);
  if (!ctx) throw new Error("useViewAs must be used inside ViewAsProvider");
  return ctx;
}

// What role the UI should pretend the user has. Falls through to the
// real role for tenants (no override available) and for landlords
// when the override is off.
export function useEffectiveRole(realRole: RealRole): EffectiveRole {
  const { viewAsUnitId } = useViewAs();
  if (realRole === "landlord" && viewAsUnitId !== null) return "tenant";
  return realRole;
}

// The unit the landlord is currently impersonating a tenant of, or
// null if not impersonating. Used by visibility filters on FAQ /
// Q&A / maintenance to mimic that tenant's view client-side.
export function useImpersonatedUnitId(): string | null {
  return useViewAs().viewAsUnitId;
}

type UnitRow = { _id: string; label: string };

// Header pill that doubles as the view-as toggle. Only renders when
// realRole === "landlord" — tenants have nothing to switch to.
// Dropdown lists "Landlord" + one entry per unit ("Tenant ·
// Upstairs", etc.) so picking the role also picks the unit context.
export function RolePill({ realRole }: { realRole: RealRole }) {
  const { viewAsUnitId, setViewAsUnitId } = useViewAs();
  const property = useProperty();
  const units = useQuery(
    anyApi.units.listForProperty,
    property ? { propertySlug: property.slug } : "skip",
  ) as UnitRow[] | undefined;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (realRole !== "landlord") return null;

  const isImpersonating = viewAsUnitId !== null;
  const impersonatedUnit = isImpersonating
    ? (units ?? []).find((u) => u._id === viewAsUnitId)
    : null;
  const label = isImpersonating
    ? impersonatedUnit
      ? `Tenant · ${impersonatedUnit.label}`
      : "Tenant"
    : "Landlord";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.18em] transition-colors ${
          isImpersonating
            ? "bg-amber-100 text-amber-900 hover:bg-amber-200"
            : "bg-slate-900 text-white hover:bg-slate-800"
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={
          isImpersonating
            ? `Viewing as tenant of ${impersonatedUnit?.label ?? "a unit"}. Click to switch.`
            : "Click to switch to a tenant view of a specific unit."
        }
      >
        <span>{label}</span>
        <ChevronDownIcon className="h-3 w-3" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 mt-2 w-56 origin-top-left rounded-lg border border-slate-200 bg-white py-1 shadow-lg z-30"
        >
          <RoleOption
            label="Landlord"
            active={!isImpersonating}
            onSelect={() => {
              setViewAsUnitId(null);
              setOpen(false);
            }}
          />
          {(units ?? []).length > 0 && (
            <div className="my-1 border-t border-slate-100" />
          )}
          {(units ?? []).map((u) => (
            <RoleOption
              key={u._id}
              label={`Tenant · ${u.label}`}
              active={viewAsUnitId === u._id}
              onSelect={() => {
                setViewAsUnitId(u._id);
                setOpen(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RoleOption({
  label,
  active,
  onSelect,
}: {
  label: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      className={`flex w-full items-center justify-between px-3 py-2 text-sm transition-colors ${
        active
          ? "text-slate-900 font-medium"
          : "text-slate-700 hover:bg-slate-50"
      }`}
    >
      <span>{label}</span>
      {active && (
        <span className="text-xs text-emerald-600" aria-hidden="true">
          ●
        </span>
      )}
    </button>
  );
}
